import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * Unified header action triggers. The header right column is
 * `<header-actions>` with three children:
 *   <search-action>  → opens the global search modal
 *   <a …>            → account page link (plain <a>, NOT a custom element —
 *                       navigation belongs on a link; login badge hydrates via
 *                       customer-state.js)
 *   <cart-action>    → opens the global cart drawer (or links to the cart page)
 *
 * search-action and cart-action are button-like: role="button" + tabindex are in
 * the SSR markup; this module wires click + Enter/Space. cart-action also owns the
 * count bubble (folded from the former cart-icon component).
 */

class SearchAction extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#open);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#open);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  #open = () => {
    const modal = document.getElementById('search-modal');
    if (modal && typeof modal.showDialog === 'function') modal.showDialog();
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.#open();
  };
}

/**
 * Cart trigger + count bubble in a single element. Opens the cart drawer when one
 * is present (drawer mode), otherwise navigates to the cart page (data-cart-url).
 *
 * @extends {Component<{ cartBubble: HTMLElement, cartBubbleText: HTMLElement, cartBubbleCount: HTMLElement }>}
 */
class CartAction extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#activate);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('pointerenter', this.#prefetch);
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  #activate = () => {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
    else if (this.dataset.cartUrl) window.location.href = this.dataset.cartUrl;
  };

  #prefetch = () => {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.prefetch === 'function') drawer.prefetch();
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.#activate();
  };

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  /** @param {CustomEvent} event */
  onCartUpdate = (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    // Additive sources report the ADDED quantity (not the cart total) —
    // product-form and the engraving sheet both add on top of the current count.
    const source = event.detail.data?.source;
    const isAdditiveSource =
      source === 'product-form-component' ||
      source === 'engraving-form' ||
      source === 'bundle-configurator' ||
      source === 'lei-subscription-cta';
    this.renderCartBubble(itemCount, isAdditiveSource);
  };

  /**
   * @param {number} itemCount
   * @param {boolean} comingFromProductForm
   * @param {boolean} [animate]
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'purelei:cart-count',
      JSON.stringify({ value: String(this.currentCartCount), timestamp: Date.now() })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /** Instant paint from the last known count (≤10s) before customer-state lands. */
  ensureCartBubbleIsCorrect = () => {
    const stored = sessionStorage.getItem('purelei:cart-count');
    const visible = this.refs.cartBubbleCount.textContent;
    if (stored === visible || stored === null) return;

    try {
      const { value, timestamp } = JSON.parse(stored);
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);
        if (count >= 0) this.renderCartBubble(count, false, false);
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('search-action')) customElements.define('search-action', SearchAction);
if (!customElements.get('cart-action')) customElements.define('cart-action', CartAction);
